"use client"

import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react"
import {
  CookieCategory,
  CookieConsent,
  defaultConsent,
  getConsent,
  saveConsent,
  setCookieWithConsent,
  getCookie,
  removeCookie,
} from "@/lib/cookies"

interface CookieContextType {
  consent: CookieConsent
  updateConsent: (newConsent: CookieConsent) => void
  hasConsent: (category: CookieCategory) => boolean
  setCookie: (
    name: string,
    value: string,
    category: CookieCategory,
    options?: any
  ) => boolean
  getCookie: (name: string) => string | undefined
  removeCookie: (name: string, options?: any) => void
}

const CookieContext = createContext<CookieContextType | undefined>(undefined)

export const CookieProvider = ({ children }: { children: ReactNode }) => {
  const [consent, setConsent] = useState<CookieConsent>(defaultConsent)

  useEffect(() => {
    setConsent(getConsent())
  }, [])

  const updateConsent = (newConsent: CookieConsent) => {
    saveConsent(newConsent)
    setConsent(newConsent)
  }

  const checkConsent = (category: CookieCategory): boolean => {
    return category === CookieCategory.NECESSARY || consent[category]
  }

  const value = {
    consent,
    updateConsent,
    hasConsent: checkConsent,
    setCookie: setCookieWithConsent,
    getCookie,
    removeCookie,
  }

  return (
    <CookieContext.Provider value={value}>{children}</CookieContext.Provider>
  )
}

export const useCookies = (): CookieContextType => {
  const context = useContext(CookieContext)
  if (context === undefined) {
    throw new Error("useCookies must be used within a CookieProvider")
  }
  return context
} 